import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type LegalKey = 'privacy' | 'terms' | null;
const legalContent: Record<Exclude<LegalKey, null>, {
  title: string;
  body: {
    heading?: string;
    text: string;
  }[];
}> = {
  privacy: {
    title: 'Privacy Policy',
    body: [{
      text: 'Your privacy matters to us. This policy outlines how UNHOOKED collects, uses, and protects your personal information when you purchase or interact with our digital products.'
    }, {
      heading: '1. Information We Collect',
      text: 'When you purchase UNHOOKED, we collect only the information necessary to process your order: your name, email address, and billing details. Payment information is handled directly by our payment processor and is never stored on our servers.'
    }, {
      heading: '2. How We Use Your Information',
      text: 'We use your information to deliver the digital product to your inbox, send order confirmations, provide customer support, and occasionally share relevant updates about UNHOOKED. We never sell, rent, or share your data with third parties for marketing purposes.'
    }, {
      heading: '3. Discreet Purchase',
      text: 'Your purchase will appear discreetly on your billing statement. We do not send physical mail, and no one is informed of your purchase except you.'
    }, {
      heading: '4. Data Security',
      text: 'We use industry-standard encryption to protect your data in transit and at rest. Access to personal information is restricted to authorized personnel only.'
    }, {
      heading: '5. Your Rights',
      text: 'You may request access to, correction of, or deletion of your personal data at any time by replying to your order confirmation email. We will respond within 30 days.'
    }, {
      heading: '6. Cookies',
      text: 'Our site uses essential cookies to maintain your session and improve your experience. We do not use third-party tracking cookies for advertising purposes.'
    }, {
      heading: '7. Updates to This Policy',
      text: 'We may update this policy from time to time. Material changes will be communicated to existing customers via email.'
    }]
  },
  terms: {
    title: 'Terms of Service',
    body: [{
      text: 'By purchasing or accessing UNHOOKED, you agree to the following terms. Please read them carefully before completing your purchase.'
    }, {
      heading: '1. Digital Product',
      text: 'UNHOOKED is a digital guide delivered electronically in PDF, EPUB, and MOBI formats. Upon successful purchase, you will receive download access via email within minutes.'
    }, {
      heading: '2. License',
      text: 'Your purchase grants you a personal, non-transferable, non-exclusive license to access and read UNHOOKED for personal use. You may not redistribute, resell, share, or publicly post the contents of the guide.'
    }, {
      heading: '3. Refund Policy',
      text: 'We offer a 30-day money-back guarantee. If the framework does not resonate with you, simply reply to your receipt email within 30 days of purchase to request a full refund. No questions asked.'
    }, {
      heading: '4. No Medical Advice',
      text: 'UNHOOKED is an educational and self-development resource based on modern neuroscience research. It is not a substitute for professional medical, psychological, or psychiatric advice, diagnosis, or treatment. Always seek the guidance of a qualified healthcare provider with questions regarding a medical or mental health condition.'
    }, {
      heading: '5. Results Disclaimer',
      text: 'Individual results vary. The testimonials shared on this site reflect the experiences of specific readers and are not guarantees of outcomes. Your engagement with the material and personal circumstances will influence your experience.'
    }, {
      heading: '6. Intellectual Property',
      text: 'All content within UNHOOKED, including text, illustrations, and design, is the intellectual property of UNHOOKED and is protected by copyright law.'
    }, {
      heading: '7. Limitation of Liability',
      text: 'To the maximum extent permitted by law, UNHOOKED and its creators shall not be liable for any indirect, incidental, or consequential damages arising from the use of this product.'
    }, {
      heading: '8. Contact',
      text: 'For any questions about these terms, simply reply to your order confirmation email.'
    }]
  }
};
function LegalModal({
  openKey,
  onClose
}: {
  openKey: LegalKey;
  onClose: () => void;
}) {
  useEffect(() => {
    if (openKey) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [openKey]);
  return <AnimatePresence data-id="element-54">
      {openKey && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" onClick={onClose} data-id="element-55">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-56" />

          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative bg-charcoal-light border border-gold/15 rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl" data-id="element-57">
            <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-gold/10" data-id="element-58">
              <h3 className="font-serif text-2xl text-ivory" data-id="element-59">
                {legalContent[openKey].title}
              </h3>
              <button onClick={onClose} aria-label="Close" className="text-ivory/50 hover:text-gold transition-colors p-1" data-id="element-60">
                <XIcon className="w-5 h-5" data-id="element-61" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 sm:px-8 py-6 space-y-5" data-id="element-62">
              {legalContent[openKey].body.map((block, i) => <div key={i} data-id="element-63">
                  {block.heading && <h4 className="text-gold/90 tracking-wider uppercase text-xs font-medium mb-2" data-id="element-64">
                      {block.heading}
                    </h4>}
                  <p className="text-ivory/75 text-sm sm:text-base font-light leading-relaxed" data-id="element-65">
                    {block.text}
                  </p>
                </div>)}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [openKey, setOpenKey] = useState<LegalKey>(null);
  return <>
      <footer className="bg-charcoal border-t border-gold/10 py-8" data-id="element-66">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-5" data-id="element-67">
          <div className="font-serif text-xl tracking-widest text-ivory/80" data-id="element-68">
            UNHOOKED
          </div>

          <div className="flex items-center gap-8 text-sm text-ivory/50" data-id="element-69">
            <button onClick={() => setOpenKey('privacy')} className="hover:text-gold transition-colors" data-id="element-70">
              Privacy
            </button>
            <button onClick={() => setOpenKey('terms')} className="hover:text-gold transition-colors" data-id="element-71">
              Terms
            </button>
          </div>

          <div className="text-sm text-ivory/40" data-id="element-72">
            © {new Date().getFullYear()} UNHOOKED. All rights reserved.
          </div>
        </div>
      </footer>

      <LegalModal openKey={openKey} onClose={() => setOpenKey(null)} data-id="element-73" />
    </>;
}